import {useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {QueryStatus} from '@reduxjs/toolkit/dist/query'
import {
  getOfferCheckSuccessLandingUrl,
  getOfferCheckTriggeredForRegularOffer,
  getOfferCheckTriggeredForSplitBooking
} from 'modules/accommodation/selectors'
import {
  resetOfferCheckTriggers,
  setLastOfferCheckFailed,
  unsubscribeOfferCheck
} from 'modules/accommodation/slice'

import {
  offerCheckApi,
  useGetOfferCheckQuery
} from '@daedalus/core/src/offer/services/offerCheckApi'
import {OfferCheckApiRequestParams} from '@daedalus/core/src/offer/types/OfferCheck'

const OfferCheckSubscription = ({
  type,
  id,
  params
}: {
  type: 'regularOffer' | 'splitBooking'
  id: string
  params: OfferCheckApiRequestParams
}) => {
  const {status, data} = useGetOfferCheckQuery(params)
  const dispatch = useDispatch()

  const landingUrl = useSelector(getOfferCheckSuccessLandingUrl)

  useEffect(() => {
    if (status === QueryStatus.rejected) {
      dispatch(setLastOfferCheckFailed(true))
    }
  }, [status, dispatch])

  useEffect(() => {
    if (status === QueryStatus.fulfilled) {
      if (landingUrl) {
        //removing self from subscribe list on successful navigation
        dispatch(unsubscribeOfferCheck({type, id}))
        window.location.href = `${landingUrl}&apRequestId=${data.httpRequestId}`
      }
    }
  }, [status, dispatch, landingUrl, data, type, id])

  return null
}

/**
 * Simple and reliable way of keeping the triggered subscriptions alive.
 * This is needed to keep the queries state so they can be checked in multiple
 * places of the app (rooms/offers list, room details overlay, etc) without
 * interfeering with the logic which triggers those offer checks
 */
export const OfferCheckSubscriptions = () => {
  const regularOffers = useSelector(getOfferCheckTriggeredForRegularOffer)
  const splitBookings = useSelector(getOfferCheckTriggeredForSplitBooking)

  const dispatch = useDispatch()
  useEffect(() => {
    //event triggered when page is served from bfcache memory snapshot
    //read more here https://web.dev/articles/bfcache
    window.addEventListener('pageshow', () => {
      //clearing subs which were tagged as successful so they could be called again on user interaction
      offerCheckApi.util.invalidateTags(['isSuccessfulOfferCheck'])

      //resetting some app state so it's ready to be used again
      dispatch(resetOfferCheckTriggers())
    })
  }, [dispatch])

  //TODO: refactor this to not split those to regular/split bookings
  return (
    <>
      {Object.entries(regularOffers).map(([key, params]) => (
        <OfferCheckSubscription
          type="regularOffer"
          id={key}
          key={key}
          params={params}
        />
      ))}
      {Object.entries(splitBookings).map(([key, params]) => (
        <OfferCheckSubscription
          type="splitBooking"
          id={key}
          key={key}
          params={params}
        />
      ))}
    </>
  )
}
